import React, { useState } from 'react';
import Layout from '../Layout';
import Plot from 'react-plotly.js';
import { analyzeCampaignsEvolution, analyzeCampaignsFromAPI } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ─────────────────────────────────────── */
/* Campaigns Evolution — port of campaigns_evolution.py */
/* ─────────────────────────────────────── */

// Color palette matching tracking2 status colors
const STATUS_STYLE = {
    Purple: { bg: '#9C27B0', color: '#fff' },
    White: { bg: '#E0E0E0', color: '#333' },
    Green: { bg: '#4CAF50', color: '#fff' },
    Red: { bg: '#F44336', color: '#fff' },
    Orange: { bg: '#FF9800', color: '#fff' },
};

function CampaignsEvolution() {
    const [files, setFiles] = useState({ w1: null, w2: null, w3: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    // Dual-mode: 'file' or 'api'
    const [dataSource, setDataSource] = useState('file');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date(); d.setDate(d.getDate() - 21);
        return d.toISOString().slice(0, 10);
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (dataSource === 'file' && (!files.w1 || !files.w2 || !files.w3)) {
            setError('Please upload all 3 weekly files');
            return;
        }
        if (dataSource === 'api') {
            const profileId = localStorage.getItem('selectedAccountValue');
            if (!profileId) {
                setError('Selecciona una cuenta de Amazon Ads en el header antes de usar el modo API.');
                return;
            }
        }
        setLoading(true);
        setError('');
        try {
            let res;
            if (dataSource === 'api') {
                const profileId = localStorage.getItem('selectedAccountValue');
                res = await analyzeCampaignsFromAPI(profileId, startDate, endDate);
            } else {
                res = await analyzeCampaignsEvolution(files.w1, files.w2, files.w3);
            }
            setResult(res.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const buildSankeyData = () => {
        if (!result?.sankey) return null;
        const { nodes, links } = result.sankey;
        return [{
            type: 'sankey',
            orientation: 'h',
            arrangement: 'freeform',
            node: {
                pad: 20,
                thickness: 20,
                line: { color: 'black', width: 0.5 },
                label: nodes.labels,
                color: nodes.colors,
                hovertemplate: 'Node: %{label}<br>Value: %{value}<extra></extra>',
            },
            link: {
                source: links.sources,
                target: links.targets,
                value: links.values,
                color: links.sources.map(i => nodes.colors[i] || '#ccc'),
                hovertemplate: 'Source: %{source.label}<br>Target: %{target.label}<br>Value: %{value}<extra></extra>',
            },
        }];
    };

    const renderStatusBadge = (status) => {
        const style = STATUS_STYLE[status] || { bg: '#9E9E9E', color: '#fff' };
        return (
            <span style={{
                display: 'inline-block',
                padding: '2px 10px',
                borderRadius: '4px',
                fontWeight: 600,
                fontSize: '0.8rem',
                backgroundColor: style.bg,
                color: style.color,
                border: status === 'White' ? '1px solid #bbb' : 'none',
            }}>
                {status || '—'}
            </span>
        );
    };

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Campaigns Evolution</h2>

                {error && <div className="alert alert-danger">{error}</div>}

                {/* Upload / API form */}
                <div className="card mb-4">
                    <div className="card-body">
                        {/* Mode Toggle */}
                        <div className="btn-group mb-3" role="group">
                            <button type="button"
                                className={`btn ${dataSource === 'file' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setDataSource('file')}>
                                <i className="bi bi-file-earmark-arrow-up me-1"></i> Upload Files
                            </button>
                            <button type="button"
                                className={`btn ${dataSource === 'api' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setDataSource('api')}>
                                <i className="bi bi-cloud-download me-1"></i> Fetch from Amazon API
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="row g-3 align-items-end">
                                {dataSource === 'file' ? (
                                    <>
                                        {['w1', 'w2', 'w3'].map((key, i) => (
                                            <div className="col-md-3" key={key}>
                                                <label className="form-label">Week {i + 1}</label>
                                                <input type="file" className="form-control form-control-sm" accept=".csv,.xlsx,.xls"
                                                    onChange={e => setFiles(prev => ({ ...prev, [key]: e.target.files[0] }))} required />
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <>
                                        <div className="col-md-3">
                                            <label className="form-label">Start Date</label>
                                            <input type="date" className="form-control" value={startDate}
                                                onChange={e => setStartDate(e.target.value)} required />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label">End Date</label>
                                            <input type="date" className="form-control" value={endDate}
                                                onChange={e => setEndDate(e.target.value)} required />
                                        </div>
                                        <div className="col-md-3">
                                            <div className="alert alert-info py-1 px-2 mb-0 small">
                                                <i className="bi bi-info-circle me-1"></i>
                                                Cuenta: <strong>{localStorage.getItem('selectedAccountName') || 'No seleccionada'}</strong>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className="col-md-3">
                                    <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                                        {loading ? <span className="spinner-border spinner-border-sm" /> : 'Analyze'}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {/* Weekly counts */}
                {result?.weeklyCounts && (
                    <div className="row mb-4 g-3">
                        {result.weeklyCounts.map(w => (
                            <div className="col-md-4" key={w.week}>
                                <div className="card text-center bg-light">
                                    <div className="card-body py-2">
                                        <small className="text-muted">{w.week}</small>
                                        <h5 className="mb-0">{w.campaigns} campaigns</h5>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Filtered Campaigns (Purple/White — Not Reviewed) ── */}
                {result?.evolution?.filtered?.length > 0 && (
                    <div className="card mb-4 border-warning">
                        <div className="card-header bg-warning bg-opacity-10 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                                Filtered Campaigns (Purple / White — Not Reviewed)
                            </h5>
                            <span className="badge bg-warning text-dark fs-6">
                                {result.evolution.filtered.length} campaigns
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Campaign</th>
                                            <th>Keyword / Target</th>
                                            <th className="text-center">W1</th>
                                            <th className="text-center">W2</th>
                                            <th className="text-center">W3</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.evolution.filtered.map((row, i) => (
                                            <tr key={i}>
                                                <td className="fw-semibold text-truncate" style={{ maxWidth: 300 }}>{row.campaign}</td>
                                                <td className="text-truncate" style={{ maxWidth: 200 }}>{row.keyword_text || '—'}</td>
                                                <td className="text-center">{renderStatusBadge(row.w1)}</td>
                                                <td className="text-center">{renderStatusBadge(row.w2)}</td>
                                                <td className="text-center">{renderStatusBadge(row.w3)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Sankey diagram */}
                {result?.sankey && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <h5>Campaign Status Transitions</h5>
                            <Plot
                                data={buildSankeyData()}
                                layout={{
                                    title: 'Status Flow: Week 1 → Week 2 → Week 3',
                                    height: 500,
                                    margin: { l: 20, r: 20, t: 50, b: 20 },
                                    font: { size: 12 },
                                }}
                                config={{ responsive: true }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>
                )}

                {/* Full Evolution table */}
                {result?.evolution?.full?.length > 0 && (
                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Full Campaign Evolution Table</h5>
                            <span className="badge bg-secondary fs-6">
                                {result.evolution.full.length} entries
                            </span>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm table-striped mb-0">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Campaign</th>
                                            <th>Keyword / Target</th>
                                            <th className="text-center">W1</th>
                                            <th className="text-center">W2</th>
                                            <th className="text-center">W3</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.evolution.full.map((row, i) => (
                                            <tr key={i}>
                                                <td className="fw-semibold text-truncate" style={{ maxWidth: 300 }}>{row.campaign}</td>
                                                <td className="text-truncate" style={{ maxWidth: 200 }}>{row.keyword_text || '—'}</td>
                                                <td className="text-center">{renderStatusBadge(row.w1)}</td>
                                                <td className="text-center">{renderStatusBadge(row.w2)}</td>
                                                <td className="text-center">{renderStatusBadge(row.w3)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default CampaignsEvolution;
